import { HANDLE_PREFIX_PATTERN, isValidUsernameShape } from '@/lib/utils/regex';

export function cleanUsername(username: string | null | undefined): string {
  if (!username) {
    return '';
  }

  // Decode URL-encoded characters first (e.g., %40 -> @)
  let cleaned = username;
  try {
    cleaned = decodeURIComponent(username);
  } catch {
    // If decoding fails, use the original string
    cleaned = username;
  }

  // Trim whitespace first
  cleaned = cleaned.trim();

  cleaned = cleaned.replace(HANDLE_PREFIX_PATTERN, '');

  // Trim again after removing @
  cleaned = cleaned.trim();

  // Return empty string if nothing left after cleaning
  if (!cleaned) {
    return '';
  }

  // Twitter usernames can only contain alphanumeric characters and underscores
  // Return empty string if the username is invalid
  if (!isValidUsernameShape(cleaned)) {
    return '';
  }

  // Twitter usernames must be 1-15 characters long
  if (cleaned.length === 0 || cleaned.length > 15) {
    return '';
  }

  return cleaned;
}

/**
 * Formats a username for display with @ symbol
 *
 * @param username - Clean username without @ symbol
 * @returns Username prefixed with @ for display, or empty string if invalid
 *
 * @example
 * formatUsername('steipete') // returns '@steipete'
 * formatUsername('') // returns ''
 */
export function formatUsername(username: string | null | undefined): string {
  const clean = cleanUsername(username);
  return clean ? `@${clean}` : '';
}

/**
 * Checks if a username is valid according to Twitter's rules
 *
 * @param username - Username to validate (with or without @)
 * @returns true if the username is valid
 *
 * @example
 * isValidUsername('steipete') // returns true
 * isValidUsername('@steipete') // returns true
 * isValidUsername('user-name') // returns false (contains hyphen)
 * isValidUsername('') // returns false
 */
export function isValidUsername(username: string | null | undefined): boolean {
  const clean = cleanUsername(username);
  if (!clean) {
    return false;
  }

  // Twitter usernames must be 1-15 characters long
  if (clean.length === 0 || clean.length > 15) {
    return false;
  }

  // Must contain only alphanumeric characters and underscores
  return isValidUsernameShape(clean);
}
